import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Mail, Phone, X } from 'lucide-react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

const ROLES = [
  'lawyer', 'accountant', 'banker', 'broker', 'borrower', 'advisor',
  'insurance', 'property_manager', 'seller', 'buyer', 'vendor', 'consultant', 'other',
];

export default function ContactsTab({ businessId, isOwner }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    const q = query(collection(db, 'businesses', businessId, 'contacts'), orderBy('name'));
    const unsub = onSnapshot(q, (snap) => {
      setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('[Contacts] snapshot:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [businessId]);

  const remove = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    await deleteDoc(doc(db, 'businesses', businessId, 'contacts', id));
  };

  const filtered = contacts.filter((c) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(f) ||
      (c.company || '').toLowerCase().includes(f) ||
      (c.role || '').toLowerCase().includes(f) ||
      (c.email || '').toLowerCase().includes(f)
    );
  });

  return (
    <div className="contacts-tab">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Contacts</h2>
        {isOwner && (
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> New Contact
          </button>
        )}
      </div>

      <input
        type="text"
        placeholder="Search name / company / role / email…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="contacts-search"
        style={{ padding: '10px 12px', width: '100%', maxWidth: 420, marginBottom: 16, border: '1px solid var(--border)', borderRadius: 8 }}
      />

      {loading ? (
        <div className="nk-skel nk-skel--line" style={{ width: '80%' }}>&nbsp;</div>
      ) : filtered.length === 0 ? (
        <div className="nk-empty-state">
          <div className="nk-empty-icon">👥</div>
          <div className="nk-empty-title">{contacts.length === 0 ? 'No contacts yet' : 'No matches'}</div>
          <div className="nk-empty-body">{contacts.length === 0 ? 'Add lawyers, brokers, accountants, or counterparties tied to this business.' : 'Try clearing the search.'}</div>
        </div>
      ) : (
        <table className="nk-table-cards" style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--muted)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '8px 10px' }}>Name</th>
              <th style={{ padding: '8px 10px' }}>Role</th>
              <th style={{ padding: '8px 10px' }}>Company</th>
              <th style={{ padding: '8px 10px' }}>Contact</th>
              <th style={{ padding: '8px 10px', width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td data-label="Name" style={{ padding: '8px 10px', fontWeight: 600 }}>{c.name}</td>
                <td data-label="Role" style={{ padding: '8px 10px' }}>{c.role || '—'}</td>
                <td data-label="Company" style={{ padding: '8px 10px' }}>{c.company || '—'}</td>
                <td data-label="Contact" style={{ padding: '8px 10px', fontSize: 12 }}>
                  {c.email && <div><Mail size={11} style={{ verticalAlign: 'middle' }} /> <a href={`mailto:${c.email}`}>{c.email}</a></div>}
                  {c.phone && <div><Phone size={11} style={{ verticalAlign: 'middle' }} /> <a href={`tel:${c.phone}`}>{c.phone}</a></div>}
                </td>
                <td data-label="" style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <button className="btn btn-sm btn-ghost" onClick={() => setEditing(c)} title="Edit"><Pencil size={13} /></button>
                  {isOwner && <button className="btn btn-sm btn-danger" onClick={() => remove(c.id)} title="Delete"><Trash2 size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(showNew || editing) && (
        <ContactForm
          contact={editing}
          businessId={businessId}
          onSave={() => { setShowNew(false); setEditing(null); }}
          onCancel={() => { setShowNew(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ContactForm({ contact, businessId, onSave, onCancel }) {
  const [name, setName] = useState(contact?.name || '');
  const [role, setRole] = useState(contact?.role || '');
  const [company, setCompany] = useState(contact?.company || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const [address, setAddress] = useState(contact?.address || '');
  const [country, setCountry] = useState(contact?.country || '');
  const [notes, setNotes] = useState(contact?.notes || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        role: role || null,
        company: company.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        country: country.trim() || null,
        notes: notes.trim() || null,
        updated_at: serverTimestamp(),
      };
      if (contact) {
        await updateDoc(doc(db, 'businesses', businessId, 'contacts', contact.id), payload);
      } else {
        payload.created_at = serverTimestamp();
        await addDoc(collection(db, 'businesses', businessId, 'contacts'), payload);
      }
      onSave();
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 480, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{contact ? 'Edit contact' : 'New contact'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}><X size={16} /></button>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--muted)' }}>
            Name
            <input required value={name} onChange={(e) => setName(e.target.value)} style={fieldStyle} />
          </label>
          <label style={labelStyle}>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value)} style={fieldStyle}>
              <option value="">—</option>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
          </label>
          <label style={labelStyle}>Company<input value={company} onChange={(e) => setCompany(e.target.value)} style={fieldStyle} /></label>
          <label style={labelStyle}>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={fieldStyle} /></label>
          <label style={labelStyle}>Phone<input value={phone} onChange={(e) => setPhone(e.target.value)} style={fieldStyle} /></label>
          <label style={labelStyle}>Country<input value={country} onChange={(e) => setCountry(e.target.value)} style={fieldStyle} /></label>
          <label style={labelStyle}>Address<input value={address} onChange={(e) => setAddress(e.target.value)} style={fieldStyle} /></label>
          <label style={labelStyle}>Notes<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={fieldStyle} /></label>
          {err && <div style={{ color: '#b54a4a', fontSize: 13, padding: 8, background: '#fdeaea', borderRadius: 6 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--muted)' };
const fieldStyle = { padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
